import React, { useState } from 'react';
import { Box, List } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import THEME from '../../constants/theme';
import ROUTES from '../../config/routes';
import { useNavigation } from '../../context/NavigationContext';
import {
  useIntersectionObserver,
  getLogo,
  getNavigationMenuItems,
  navigateToPathOrSection
} from '../../utils/navigationUtils';

import NavigationMenuItem from './NavigationMenuItem';
import ServiceStatusModal from '../common/ServiceStatusModal';

const NavigationMenu = ({
  pageSectionRefsContainer,
  textColor = '#000000',
  width = 250,
  isHomePage = false
}) => {
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const {
    expandedMenu,
    handleMenuToggle,
    activeSection,
    setActiveSection,
    startPageTransition
  } = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = getNavigationMenuItems();
  const activeColor = THEME.COLORS.PRIMARY_PURPLE;

  useIntersectionObserver(
    pageSectionRefsContainer ? pageSectionRefsContainer.current : {},
    setActiveSection
  );

  const handleNavigate = path => {
    const currentRefs = pageSectionRefsContainer
      ? pageSectionRefsContainer.current
      : {};
    navigateToPathOrSection(
      null,
      path,
      currentRefs,
      navigate,
      location.pathname,
      startPageTransition
    );
  };

  const handleSectionScroll = (sectionId, parentPath) => {
    const currentRefs = pageSectionRefsContainer
      ? pageSectionRefsContainer.current
      : {};
    navigateToPathOrSection(
      sectionId,
      parentPath,
      currentRefs,
      navigate,
      location.pathname,
      startPageTransition
    );
  };

  const handleServiceStatusClick = () => {
    setServiceModalOpen(true);
  };

  const handleServiceModalClose = () => {
    setServiceModalOpen(false);
  };

  const logoSrc = getLogo(textColor);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        height: '100vh',
        width: width,
        background: isHomePage 
          ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        color: textColor,
        display: {
          xs: 'none',
          sm: 'none',
          md: 'block'
        },
        borderLeft: isHomePage ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
        paddingTop: isHomePage ? '8vh' : '3vh',
        paddingRight: '2vh',
        paddingLeft: '1vh',
        zIndex: 10,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxShadow: isHomePage ? 'none' : '0 2px 20px rgba(0, 0, 0, 0.1)'
      }}
    >
      <List sx={{ 
        padding: '20px 0',
        '& .MuiListItem-root': {
          borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
          margin: '4px 0',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: isHomePage 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(101, 17, 175, 0.05)',
            transform: 'translateX(-4px)'
          }
        }
      }}>
        {menuItems.map(item => (
          <React.Fragment key={item.label}>
            <NavigationMenuItem
              item={item}
              expandedMenu={expandedMenu}
              handleMenuToggle={handleMenuToggle}
              handleScroll={handleSectionScroll}
              location={location}
              activeSection={activeSection}
              textColor={textColor}
              activeColor={activeColor}
              startPageTransition={startPageTransition}
              navigate={navigate}
              onServiceStatusClick={handleServiceStatusClick}
            />
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ margin: '20px 0' }} />

      {/* Logo Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '15px 5px',
          cursor: 'pointer',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)'
          }
        }}
        onClick={() => handleNavigate(ROUTES.HOME)}
      >
        <img
          src={logoSrc}
          alt='OctopusSync Logo'
          style={{
            width: '100%',
            maxWidth: `${width * 0.95}px`,
            height: 'auto',
            filter: textColor === '#ffffff' ? 'none' : 'none'
          }}
        />
      </Box>

      {/* Service Status Modal */}
      <ServiceStatusModal
        open={serviceModalOpen}
        onClose={handleServiceModalClose}
      />
    </Box>
  );
};

NavigationMenu.propTypes = {
  pageSectionRefsContainer: PropTypes.object,
  textColor: PropTypes.string,
  width: PropTypes.number
};

export default NavigationMenu;
